import { legacyLocalLabel } from '../../../utilities/legacyDatePresentation';
import React, { useEffect, useState } from 'react';
import { CalendarDays, Users, Building2, Loader2, IdCardLanyard, ShieldCheck } from 'lucide-react';
import { useUserProfileCache } from '../../../contexts/UserProfileCacheContext'; // Adjust path
import AvatarImage from '../Avatar/AvatarImage';

// 1. Update the interface to expect the new Object structures
export interface TooltipUser {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    joinDate?: Date | string;
    participantCount?: number;
    managerName?: string;
    organization?: {
        id: number;
        name: string;
        abbreviation?: string;
    };
    organizationRole?: {
        id: number;
        name: string;
        description?: string;
        systemKey?: string;
    };
}

export interface UserProfileTooltipProps {
    member: TooltipUser;
    theme?: 'light' | 'dark';
}

const UserProfileTooltip: React.FC<UserProfileTooltipProps> = ({
    member,
    theme = 'dark'
}) => {
    const { getUserProfile } = useUserProfileCache();
    const [fullData, setFullData] = useState<TooltipUser | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const isDark = theme === 'dark';
    const borderClass = isDark ? 'border-[#3a3a3c]' : 'border-gray-100';
    const primaryText = isDark ? 'text-[#f9f9fb]' : 'text-gray-900';
    const secondaryText = isDark ? 'text-[#ebebf5]/60' : 'text-gray-500';
    const iconColor = isDark ? 'text-[#ebebf5]/60' : 'text-gray-400';
    const valueText = isDark ? 'text-[#ebebf5]/80' : 'text-gray-700';
    const highlightColor = isDark ? 'text-[#a78bfa]' : 'text-[#7E57C2]';

    useEffect(() => {
        // Skip fetch if we already have the deep data objects
        if (member.joinDate || member.organization || member.managerName || member.organizationRole) {
            setFullData(member);
            return;
        }

        let isMounted = true;

        const fetchFullDetails = async () => {
            setIsLoading(true);
            try {
                const cachedProfile = await getUserProfile(member.id);
                if (isMounted && cachedProfile) {
                    setFullData(cachedProfile);
                }
            } catch (error) {
                console.error("Failed to fetch rich tooltip data:", error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchFullDetails();

        return () => {
            isMounted = false;
        };
    }, [member, getUserProfile]);

    const displayData = fullData || member;

    return (
        <div className="flex flex-col gap-3 w-64 p-1 transition-all duration-300">
            <div className={`flex items-center gap-3 border-b pb-3 ${borderClass}`}>
                <AvatarImage userId={member.id} size="sm" />
                <div className="overflow-hidden">
                    <h4 className={`font-semibold text-sm m-0 truncate ${primaryText}`}>
                        {member.firstName} {member.lastName}
                    </h4>
                    <p className={`text-xs m-0 truncate ${secondaryText}`}>
                        {displayData.email || member.email}
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-2 text-xs font-poppins min-h-[60px] relative justify-center">
                {isLoading && !fullData ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 size={18} className={`animate-spin ${iconColor}`} />
                    </div>
                ) : (
                    <div className="animate-in fade-in duration-300 flex flex-col gap-2">

                        {/* 1. Organization Role Object */}
                        {displayData.organizationRole?.name && (
                            <div className={`flex items-center justify-between font-medium ${highlightColor}`}>
                                <span className="flex items-center gap-1.5 opacity-90">
                                    <ShieldCheck size={14} /> Org Role
                                </span>
                                <span className="truncate max-w-[120px]" title={displayData.organizationRole.name}>
                                    {displayData.organizationRole.name}
                                </span>
                            </div>
                        )}

                        {/* 2. Organization Object */}
                        {displayData.organization?.name && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <Building2 size={14} /> Organization
                                </span>
                                <span className="truncate max-w-[120px] text-right" title={displayData.organization.name}>
                                    {displayData.organization.name}
                                </span>
                            </div>
                        )}

                        {/* 3. Joined Date */}
                        {displayData.joinDate && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <CalendarDays size={14} /> Joined
                                </span>
                                {/* Strictly enforced American Date Format */}
                                <span>{legacyLocalLabel(displayData.joinDate, "MM/dd/yyyy")}</span>
                            </div>
                        )}

                        {/* 4. Manager */}
                        {displayData.managerName && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <IdCardLanyard size={14} /> Manager
                                </span>
                                <span className="truncate max-w-[120px]" title={displayData.managerName}>
                                    {displayData.managerName}
                                </span>
                            </div>
                        )}

                        {/* 5. Participants */}
                        {displayData.participantCount !== undefined && (
                            <div className={`flex items-center justify-between ${valueText}`}>
                                <span className={`flex items-center gap-1.5 ${iconColor}`}>
                                    <Users size={14} /> Participants
                                </span>
                                <span>{displayData.participantCount}</span>
                            </div>
                        )}

                        {/* Fallback */}
                        {!displayData.joinDate && !displayData.organization && displayData.participantCount === undefined && !displayData.managerName && !displayData.organizationRole && (
                            <div className={`text-center py-2 italic ${secondaryText}`}>
                                No additional details available
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserProfileTooltip;